/** @format */
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import config from "../config.json";

/**
 * Récupère une couleur pour l'environnement courant depuis config.json.
 * Version "lecture directe" de setEnvironmentColors, utilisable en dehors
 * d'un composant React (pas de setState ici).
 */
function getEnvironmentColor(colorKey, fallback) {
  const environment = process.env.REACT_APP_DEV_NAME;
  const colors = config[environment];
  if (colors && colors[colorKey]) {
    return colors[colorKey];
  }
  console.warn(`Environnement non configuré dans config.json pour ${colorKey}`);
  return fallback;
}

/**
 * Affiche une popup bloquante prévenant la déconnexion.
 * Reste affichée jusqu'à ce que l'utilisateur clique sur "OK",
 * puis exécute onClose (nettoyage + redirection).
 */
function showSessionExpiredModal(onClose) {
  if (document.getElementById("session-expired-modal")) return;

  if (!document.getElementById("session-expired-modal-styles")) {
    const style = document.createElement("style");
    style.id = "session-expired-modal-styles";
    style.textContent = `
      @keyframes sem-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes sem-scale-in {
        from { opacity: 0; transform: scale(0.92) translateY(8px); }
        to { opacity: 1; transform: scale(1) translateY(0); }
      }
    `;
    document.head.appendChild(style);
  }

  // Couleur principale = couleur d'environnement (fallback bleu si absente)
  const accentColor = getEnvironmentColor("headerColor", "#2563eb");

  const overlay = document.createElement("div");
  overlay.id = "session-expired-modal";
  overlay.style.position = "fixed";
  overlay.style.inset = "0";
  overlay.style.backgroundColor = "rgba(15, 23, 42, 0.55)";
  overlay.style.backdropFilter = "blur(4px)";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.zIndex = "10000";
  overlay.style.animation = "sem-fade-in 0.2s ease-out";

  const box = document.createElement("div");
  box.style.backgroundColor = "#ffffff";
  box.style.color = "#1e293b";
  box.style.padding = "32px 32px 24px";
  box.style.borderRadius = "16px";
  box.style.maxWidth = "380px";
  box.style.width = "90%";
  box.style.textAlign = "center";
  box.style.boxShadow = "0 20px 50px rgba(0, 0, 0, 0.25)";
  box.style.fontFamily = "'Segoe UI', Roboto, Arial, sans-serif";
  box.style.animation = "sem-scale-in 0.25s ease-out";
  box.style.borderTop = `4px solid ${accentColor}`;

  const iconWrapper = document.createElement("div");
  iconWrapper.style.width = "56px";
  iconWrapper.style.height = "56px";
  iconWrapper.style.margin = "20px auto 20px";
  iconWrapper.style.borderRadius = "50%";
  iconWrapper.style.backgroundColor = `${accentColor}1a`; // accent en très clair (alpha hex)
  iconWrapper.style.display = "flex";
  iconWrapper.style.alignItems = "center";
  iconWrapper.style.justifyContent = "center";

  iconWrapper.innerHTML = `
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 0 0 3.54 21H20.46A2 2 0 0 0 22.18 18L13.71 3.86A2 2 0 0 0 10.29 3.86Z"
        stroke="${accentColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  const title = document.createElement("h3");
  title.textContent = "Session expirée";
  title.style.margin = "0 0 8px";
  title.style.fontSize = "18px";
  title.style.fontWeight = "600";

  const message = document.createElement("p");
  message.textContent = "Votre session a expiré. Vous allez être redirigé vers la page de connexion.";
  message.style.margin = "0 0 24px";
  message.style.fontSize = "14px";
  message.style.lineHeight = "1.5";
  message.style.color = "#64748b";

  const button = document.createElement("button");
  button.textContent = "Se reconnecter";
  button.style.padding = "10px 28px";
  button.style.backgroundColor = accentColor;
  button.style.color = "#ffffff";
  button.style.border = "none";
  button.style.borderRadius = "8px";
  button.style.cursor = "pointer";
  button.style.fontSize = "14px";
  button.style.fontWeight = "600";
  button.style.transition = "filter 0.15s ease";

  button.addEventListener("mouseenter", () => {
    button.style.filter = "brightness(0.9)";
  });
  button.addEventListener("mouseleave", () => {
    button.style.filter = "none";
  });

  button.addEventListener("click", () => {
    document.body.removeChild(overlay);
    onClose();
  });

  box.appendChild(iconWrapper);
  box.appendChild(title);
  box.appendChild(message);
  box.appendChild(button);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

/**
 * Wrapper central pour tous les appels API.
 * - Si 401 : affiche une popup bloquante, puis nettoie la session et redirige vers /
 * - Si autre erreur HTTP ou réseau : affiche un toast d'erreur
 * - Si OK : retourne le JSON parsé
 */
export async function apiRequest(url, options = {}) {
  let response;

  try {
    response = await fetch(url, options);
  } catch (networkError) {
    toast.error("Erreur réseau : impossible de contacter le serveur.");
    throw networkError;
  }

  if (response.status === 401) {
    console.log("[apiRequest] 401 reçu → session expirée, affichage popup");

    showSessionExpiredModal(() => {
      Cookies.remove("Token");
      localStorage.clear();
      window.location.href = "/";
    });

    throw new Error("Unauthorized (401)");
  }

  if (!response.ok) {
    let message = `Erreur ${response.status}`;
    try {
      const errorBody = await response.json();
      message = errorBody?.message || message;
    } catch {
      // le corps n'était pas du JSON, on garde le message par défaut
    }
    toast.error(message);
    throw new Error(message);
  }

  return response.json();
}